import { ref, computed, onUnmounted } from 'vue'

export function useImageSelection(gridContainerRef, { folders, images, filteredFolders, filteredImages }) {
  const selectedItems = ref([])
  const selectedImageCount = computed(() => selectedItems.value.filter(item => item.type === 'image').length)
  const selectionRectangle = ref({
    active: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0
  })
  const itemRefs = ref({})

  // Auto-scroll state
  const autoScrollState = ref({
    active: false,
    frameId: null,
    velocityY: 0,
    lastMouseClientY: 0
  })

  // Unified key generator
  const makeItemKey = (type, id) => `${type}-${String(id)}`

  const setItemRef = (el, item, type) => {
    if (el && item?.id != null) {
      itemRefs.value[makeItemKey(type, item.id)] = el
    }
  }

  const isSelected = (item, type) => {
    if (!item?.id) return false
    const idStr = String(item.id)
    return selectedItems.value.some(selected =>
      String(selected.id) === idStr && selected.type === type
    )
  }

  const toggleSelection = (item, type) => {
    if (!item?.id) return
    const idStr = String(item.id)
    const index = selectedItems.value.findIndex(selected =>
      String(selected.id) === idStr && selected.type === type
    )
    if (index >= 0) {
      selectedItems.value.splice(index, 1)
    } else {
      selectedItems.value.push({ ...item, id: item.id, type })
    }
  }

  const clearSelection = () => {
    selectedItems.value = []
  }

  const selectRange = (item, type) => {
    if (selectedItems.value.length === 0) {
      toggleSelection(item, type)
      return
    }

    const lastSelected = selectedItems.value[selectedItems.value.length - 1]
    const allItems = [
      ...filteredFolders.value.map(f => ({ ...f, type: 'folder' })),
      ...filteredImages.value.map(i => ({ ...i, type: 'image' }))
    ]

    const startIndex = allItems.findIndex(i =>
      i.id === lastSelected.id && lastSelected.type === (i.type || 'image')
    )
    const endIndex = allItems.findIndex(i =>
      i.id === item.id && type === (i.type || 'image')
    )

    if (startIndex >= 0 && endIndex >= 0) {
      const start = Math.min(startIndex, endIndex)
      const end = Math.max(startIndex, endIndex)

      for (let idx = start; idx <= end; idx++) {
        const currentItem = allItems[idx]
        const currentType = currentItem.type || 'image'
        if (!isSelected(currentItem, currentType)) {
          selectedItems.value.push({ ...currentItem, type: currentType })
        }
      }
    }
  }

  const selectAll = () => {
    const allFolders = filteredFolders.value.map(f => ({ ...f, type: 'folder' }))
    const allImages = filteredImages.value.map(i => ({ ...i, type: 'image' }))
    selectedItems.value = [...allFolders, ...allImages]
    return { count: selectedItems.value.length, imageCount: allImages.length }
  }

  // Selection rectangle style
  const selectionRectangleStyle = computed(() => {
    if (!selectionRectangle.value.active) return {}

    const left = Math.min(selectionRectangle.value.startX, selectionRectangle.value.currentX)
    const top = Math.min(selectionRectangle.value.startY, selectionRectangle.value.currentY)
    const width = Math.abs(selectionRectangle.value.currentX - selectionRectangle.value.startX)
    const height = Math.abs(selectionRectangle.value.currentY - selectionRectangle.value.startY)

    return {
      position: 'absolute',
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
      border: '1px dashed var(--primary-color)',
      backgroundColor: 'rgba(var(--primary-color-rgb), 0.05)',
      pointerEvents: 'none',
      zIndex: 300
    }
  })

  // Rectangle selection
  let _selectionRafId = null

  const _doUpdateSelectionIntersections = () => {
    const container = gridContainerRef.value
    if (!container || !selectionRectangle.value.active) return
    const rect = container.getBoundingClientRect()

    const rectLeft = Math.min(selectionRectangle.value.startX, selectionRectangle.value.currentX)
    const rectTop = Math.min(selectionRectangle.value.startY, selectionRectangle.value.currentY)
    const rectRight = Math.max(selectionRectangle.value.startX, selectionRectangle.value.currentX)
    const rectBottom = Math.max(selectionRectangle.value.startY, selectionRectangle.value.currentY)

    const newSelected = []
    Object.entries(itemRefs.value).forEach(([key, element]) => {
      if (!element) return

      const elementRect = element.getBoundingClientRect()
      const elementLeft = elementRect.left - rect.left
      const elementTop = elementRect.top - rect.top
      const elementRight = elementRect.right - rect.left
      const elementBottom = elementRect.bottom - rect.top

      if (rectLeft < elementRight && rectRight > elementLeft &&
          rectTop < elementBottom && rectBottom > elementTop) {
        const dashIndex = key.indexOf('-')
        const type = key.substring(0, dashIndex)
        const rawId = key.substring(dashIndex + 1)
        const item = type === 'folder'
          ? folders.value.find(f => String(f.id) === rawId)
          : images.value.find(i => String(i.id) === rawId)
        if (item) {
          newSelected.push({ ...item, type })
        }
      }
    })
    selectedItems.value = newSelected
  }

  const updateSelectionIntersections = () => {
    if (_selectionRafId) return
    _selectionRafId = requestAnimationFrame(() => {
      _selectionRafId = null
      _doUpdateSelectionIntersections()
    })
  }

  const stopAutoScroll = () => {
    const state = autoScrollState.value
    state.active = false
    if (state.frameId) {
      cancelAnimationFrame(state.frameId)
      state.frameId = null
    }
  }

  const startAutoScroll = (velocityY) => {
    const state = autoScrollState.value
    state.velocityY = velocityY
    if (state.active) return
    state.active = true
    const step = () => {
      if (!state.active) return
      const container = gridContainerRef.value
      if (!container || !selectionRectangle.value.active) {
        stopAutoScroll()
        return
      }
      container.scrollTop = Math.max(0, Math.min(container.scrollHeight - container.clientHeight, container.scrollTop + state.velocityY))

      const rect = container.getBoundingClientRect()
      const scrollTop = container.scrollTop || 0
      selectionRectangle.value.currentY = state.lastMouseClientY - rect.top + scrollTop
      updateSelectionIntersections()
      state.frameId = requestAnimationFrame(step)
    }
    state.frameId = requestAnimationFrame(step)
  }

  const updateSelectionRectangle = (event) => {
    if (!selectionRectangle.value.active) return
    const container = gridContainerRef.value
    if (!container) return

    const rect = container.getBoundingClientRect()
    const scrollTop = container.scrollTop || 0
    const scrollLeft = container.scrollLeft || 0

    selectionRectangle.value.currentX = event.clientX - rect.left + scrollLeft
    selectionRectangle.value.currentY = event.clientY - rect.top + scrollTop

    updateSelectionIntersections()

    autoScrollState.value.lastMouseClientY = event.clientY

    const edge = 40
    let velocityY = 0
    if (event.clientY > rect.bottom - edge) {
      const intensity = Math.min(1, (event.clientY - (rect.bottom - edge)) / edge)
      velocityY = 8 + 24 * intensity
    } else if (event.clientY < rect.top + edge) {
      const intensity = Math.min(1, ((rect.top + edge) - event.clientY) / edge)
      velocityY = -(8 + 24 * intensity)
    }

    if (velocityY !== 0) {
      startAutoScroll(velocityY)
    } else {
      stopAutoScroll()
    }
  }

  const endSelectionRectangle = () => {
    selectionRectangle.value.active = false
    document.removeEventListener('mousemove', updateSelectionRectangle)
    document.removeEventListener('mouseup', endSelectionRectangle)
    stopAutoScroll()
  }

  const startSelectionRectangle = (event) => {
    if (event.target.closest('.selectable-item') || event.button !== 0) return

    event.preventDefault()
    const container = gridContainerRef.value
    if (!container) return

    const rect = container.getBoundingClientRect()
    const scrollTop = container.scrollTop || 0
    const scrollLeft = container.scrollLeft || 0

    selectionRectangle.value = {
      active: true,
      startX: event.clientX - rect.left + scrollLeft,
      startY: event.clientY - rect.top + scrollTop,
      currentX: event.clientX - rect.left + scrollLeft,
      currentY: event.clientY - rect.top + scrollTop
    }

    if (!event.ctrlKey && !event.metaKey) {
      clearSelection()
    }

    document.addEventListener('mousemove', updateSelectionRectangle)
    document.addEventListener('mouseup', endSelectionRectangle)
  }

  // Cleanup on unmount to prevent leaked document listeners
  onUnmounted(() => {
    endSelectionRectangle()
  })

  return {
    selectedItems, selectedImageCount,
    selectionRectangle, selectionRectangleStyle,
    itemRefs, isSelected, toggleSelection, clearSelection,
    selectRange, selectAll, setItemRef,
    startSelectionRectangle
  }
}
